import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase-auth';
import { showError, showSuccess } from '../../../utils/toast';
import { invitationService } from '../../../services/invitationService';
import {
  EnvelopeIcon,
  EyeIcon,
  LinkIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  KeyIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

interface InvitationData {
  id: string;
  email: string;
  full_name: string;
  party_id?: string;
  party_name?: string;
  party_type?: string;
  invited_by: string;
  invited_by_name?: string;
  invited_by_email?: string;
  token: string;
  expires_at: string;
  accepted_at?: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  created_at: string;
  updated_at: string;
  tracking_status?: string;
  hours_until_expiry?: number;
  days_since_created?: number;
  
  // New tracking fields
  email_sent_at?: string;
  email_opened_at?: string;
  email_opened_count?: number;
  link_clicked_at?: string;
  link_clicked_count?: number;
  last_activity_at?: string;
  ip_address?: string;
  user_agent?: string;
  resent_count?: number;
  last_resent_at?: string;
  notes?: string;
  reminder_sent_at?: string;
  password_set_at?: string;
  
  // Client user data
  client_user_id?: string;
  client_last_login?: string;
  client_is_active?: boolean;
}

interface InvitationStats {
  total_sent: number;
  total_accepted: number;
  total_pending: number;
  total_expired: number;
  total_cancelled: number;
  acceptance_rate: number;
  avg_time_to_accept?: string;
  emails_opened: number;
  links_clicked: number;
  engagement_rate: number;
}

interface InvitationTrackerProps {
  userId?: string;
  showAllInvitations?: boolean;
  onClose?: () => void;
}

export const InvitationTracker: React.FC<InvitationTrackerProps> = ({
  userId,
  showAllInvitations = false,
  onClose
}) => {
  const [invitations, setInvitations] = useState<InvitationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<InvitationStats | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<InvitationData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'accepted' | 'expired' | 'cancelled' | 'engaged'>('all');
  const [showNotes, setShowNotes] = useState<{ [key: string]: boolean }>({});
  const [editingNotes, setEditingNotes] = useState<{ [key: string]: string }>({});
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState('');

  // Fetch invitations
  const fetchInvitations = async () => {
    try {
      let query = supabase
        .from('client_invitation_tracking')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by user if not showing all
      if (!showAllInvitations && userId) {
        query = query.eq('invited_by', userId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setInvitations(data || []);

      // Fetch statistics
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_invitation_statistics', { 
          p_user_id: showAllInvitations ? null : userId 
        });

      if (!statsError && statsData && statsData.length > 0) {
        setStats(statsData[0]);
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error fetching invitations:', err);
      }
      showError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('invitation-updates')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'client_invitations'
      }, () => {
        fetchInvitations();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId, showAllInvitations]);

  // Filter invitations
  const filteredInvitations = useMemo(() => {
    return invitations.filter(inv => {
      const matchesSearch = 
        inv.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (inv.party_name && inv.party_name.toLowerCase().includes(searchTerm.toLowerCase()));

      let matchesStatus = true;
      if (filterStatus !== 'all') {
        if (filterStatus === 'engaged') {
          matchesStatus = inv.email_opened_at !== null || inv.link_clicked_at !== null;
        } else {
          matchesStatus = inv.status === filterStatus;
        }
      }

      return matchesSearch && matchesStatus;
    });
  }, [invitations, searchTerm, filterStatus]);

  // Handle resend invitation
  const handleResend = async (invitation: InvitationData) => {
    try {
      const result = await invitationService.resendInvitation(invitation.id);
      if (result.success) {
        showSuccess('Invitation resent successfully');
        fetchInvitations();
      } else {
        showError(result.error || 'Failed to resend invitation');
      }
    } catch (err) {
      showError('Failed to resend invitation');
    }
  };

  // Handle cancel invitation
  const handleCancel = async (invitation: InvitationData) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return;
    
    try {
      const result = await invitationService.cancelInvitation(invitation.id);
      if (result.success) {
        showSuccess('Invitation cancelled');
        fetchInvitations();
      } else {
        showError(result.error || 'Failed to cancel invitation');
      }
    } catch (err) {
      showError('Failed to cancel invitation');
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!passwordResetEmail) return;
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(passwordResetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      
      showSuccess('Password reset email sent successfully');
      setShowPasswordResetModal(false);
      setPasswordResetEmail('');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error sending password reset:', err);
      }
      showError('Failed to send password reset email');
    }
  };

  // Handle notes update
  const handleUpdateNotes = async (invitationId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('client_invitations')
        .update({ notes })
        .eq('id', invitationId);

      if (error) throw error;
      
      showSuccess('Notes updated');
      setEditingNotes(prev => ({ ...prev, [invitationId]: '' }));
      setShowNotes(prev => ({ ...prev, [invitationId]: false }));
      fetchInvitations();
    } catch (err) {
      showError('Failed to update notes');
    }
  };

  // Get status badge
  const getStatusBadge = (invitation: InvitationData) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    if (invitation.status === 'accepted') {
      return (
        <span className={`${baseClasses} bg-green-100 text-green-800`}>
          <CheckCircleSolid className="w-3 h-3 mr-1" />
          Accepted
        </span>
      );
    }
    
    if (invitation.status === 'cancelled') {
      return (
        <span className={`${baseClasses} bg-red-100 text-red-800`}>
          <XCircleIcon className="w-3 h-3 mr-1" />
          Cancelled
        </span>
      );
    }
    
    if (invitation.status === 'expired' || (invitation.expires_at && new Date(invitation.expires_at) < new Date())) {
      return (
        <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
          <ClockIcon className="w-3 h-3 mr-1" />
          Expired
        </span>
      );
    }
    
    // Pending with engagement info
    if (invitation.link_clicked_at) {
      return (
        <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
          <LinkIcon className="w-3 h-3 mr-1" />
          Link Clicked
        </span>
      );
    }
    
    if (invitation.email_opened_at) {
      return (
        <span className={`${baseClasses} bg-indigo-100 text-indigo-800`}>
          <EyeIcon className="w-3 h-3 mr-1" />
          Email Opened
        </span>
      );
    }
    
    return (
      <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
        <ClockIcon className="w-3 h-3 mr-1" />
        Pending
      </span>
    );
  };

  // Format date
  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  // Format relative time
  const formatRelativeTime = (date: string | null | undefined) => {
    if (!date) return '-';
    const diff = Date.now() - new Date(date).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <EnvelopeIcon className="h-8 w-8 text-gray-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Sent</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total_sent}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Accepted</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.total_accepted}
                  <span className="text-sm text-gray-500 ml-1">({stats.acceptance_rate}%)</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total_pending}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Engagement</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.engagement_rate}%
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <LinkIcon className="h-8 w-8 text-indigo-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Links Clicked</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.links_clicked}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by email, name, or organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        >
          <option value="all">All Invitations</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
          <option value="engaged">Engaged (Opened/Clicked)</option>
        </select>
      </div>

      {/* Invitations Table */}
      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recipient
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Engagement
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timeline
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredInvitations.map((invitation) => (
              <React.Fragment key={invitation.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{invitation.full_name}</div>
                      <div className="text-sm text-gray-500">{invitation.email}</div>
                      {invitation.party_name && (
                        <div className="text-xs text-gray-400 mt-1">
                          {invitation.party_type}: {invitation.party_name}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {getStatusBadge(invitation)}
                      {invitation.client_user_id && invitation.client_is_active && (
                        <div className="text-xs text-green-600">
                          <CheckCircleIcon className="w-3 h-3 inline mr-1" />
                          User Active
                        </div>
                      )}
                      {invitation.password_set_at && (
                        <div className="text-xs text-blue-600">
                          <KeyIcon className="w-3 h-3 inline mr-1" />
                          Password Set
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1 text-xs">
                      {invitation.email_sent_at && (
                        <div className="flex items-center text-gray-600">
                          <EnvelopeIcon className="w-3 h-3 mr-1" />
                          Sent {formatRelativeTime(invitation.email_sent_at)}
                        </div>
                      )}
                      {invitation.email_opened_at && (
                        <div className="flex items-center text-blue-600">
                          <EyeIcon className="w-3 h-3 mr-1" />
                          Opened {invitation.email_opened_count}x
                        </div>
                      )}
                      {invitation.link_clicked_at && (
                        <div className="flex items-center text-indigo-600">
                          <LinkIcon className="w-3 h-3 mr-1" />
                          Clicked {invitation.link_clicked_count}x
                        </div>
                      )}
                      {invitation.resent_count && invitation.resent_count > 0 && (
                        <div className="flex items-center text-orange-600">
                          <ArrowPathIcon className="w-3 h-3 mr-1" />
                          Resent {invitation.resent_count}x
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1 text-xs text-gray-500">
                      <div>Created: {formatRelativeTime(invitation.created_at)}</div>
                      {invitation.accepted_at && (
                        <div className="text-green-600">
                          Accepted: {formatRelativeTime(invitation.accepted_at)}
                        </div>
                      )}
                      {invitation.status === 'pending' && invitation.hours_until_expiry && (
                        <div className={invitation.hours_until_expiry < 12 ? 'text-red-600' : 'text-yellow-600'}>
                          Expires in {Math.round(invitation.hours_until_expiry)}h
                        </div>
                      )}
                      {invitation.client_last_login && (
                        <div className="text-blue-600">
                          Last login: {formatRelativeTime(invitation.client_last_login)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {invitation.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleResend(invitation)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Resend invitation"
                          >
                            <PaperAirplaneIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleCancel(invitation)}
                            className="text-red-600 hover:text-red-900"
                            title="Cancel invitation"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {invitation.client_user_id && (
                        <button
                          onClick={() => {
                            setPasswordResetEmail(invitation.email);
                            setShowPasswordResetModal(true);
                          }}
                          className="text-amber-600 hover:text-amber-900"
                          title="Send password reset"
                        >
                          <KeyIcon className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowNotes(prev => ({ ...prev, [invitation.id]: !prev[invitation.id] }));
                          setEditingNotes(prev => ({ ...prev, [invitation.id]: invitation.notes || '' }));
                        }}
                        className="text-gray-600 hover:text-gray-900"
                        title="View/Edit notes"
                      >
                        <DocumentTextIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setSelectedInvitation(invitation)}
                        className="text-gray-600 hover:text-gray-900"
                        title="View details"
                      >
                        <InformationCircleIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
                {showNotes[invitation.id] && (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 bg-gray-50">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Notes
                        </label>
                        <textarea
                          value={editingNotes[invitation.id] || ''}
                          onChange={(e) => setEditingNotes(prev => ({ ...prev, [invitation.id]: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                          rows={3}
                          placeholder="Add notes about this invitation..."
                        />
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setShowNotes(prev => ({ ...prev, [invitation.id]: false }))}
                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateNotes(invitation.id, editingNotes[invitation.id] || '')}
                            className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600"
                          >
                            Save Notes
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Invitation Details
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedInvitation.full_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedInvitation.email}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">{getStatusBadge(selectedInvitation)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Invited By</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {selectedInvitation.invited_by_name} ({selectedInvitation.invited_by_email})
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(selectedInvitation.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Expires</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(selectedInvitation.expires_at)}</dd>
                </div>
                {selectedInvitation.accepted_at && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Accepted</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(selectedInvitation.accepted_at)}</dd>
                  </div>
                )}
                {selectedInvitation.ip_address && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">IP Address</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedInvitation.ip_address}</dd>
                  </div>
                )}
                {selectedInvitation.user_agent && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">User Agent</dt>
                    <dd className="mt-1 text-sm text-gray-900 break-all">{selectedInvitation.user_agent}</dd>
                  </div>
                )}
                {selectedInvitation.notes && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Notes</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedInvitation.notes}</dd>
                  </div>
                )}
              </dl>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedInvitation(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Send Password Reset
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Send a password reset email to: <strong>{passwordResetEmail}</strong>
              </p>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setPasswordResetEmail('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordReset}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-md hover:bg-amber-600"
                >
                  Send Reset Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};